// Saved views on the web (OEM-268): the API, and the board's Filters to and from a view's spec. What a
// valid spec is, how items match one and the quick views are core's (@boem/core/views).

import type { Lane } from "@boem/core/shared";
import { DEFAULT_SPEC, quickOf, quickSpec, QUICK_PREFIX, QUICK_VIEWS, type QuickViewId, type SavedViewView, type ViewGroup, type ViewSpec } from "@boem/core/views";
import { call } from "./api.ts";
import type { GroupBy } from "./grouping.ts";
import { apiBase } from "./org.ts";
import { initialFilters, type Filters } from "./filters.ts";

export const viewsApi = {
  list: () => call<SavedViewView[]>("GET", `${apiBase()}/views`),
  get: (id: string) => call<SavedViewView>("GET", `${apiBase()}/views/${encodeURIComponent(id)}`),
  create: (v: { name: string; filters: ViewSpec; shared: boolean }) => call<SavedViewView>("POST", `${apiBase()}/views`, v),
  update: (id: string, v: { name?: string; filters?: ViewSpec; shared?: boolean }) => call<SavedViewView>("PATCH", `${apiBase()}/views/${encodeURIComponent(id)}`, v),
  remove: (id: string) => call<{ ok: true }>("DELETE", `${apiBase()}/views/${encodeURIComponent(id)}`),
  reorder: (list: "shared" | "mine", order: string[]) => call<SavedViewView[]>("PUT", `${apiBase()}/views/order`, { list, order }),
};

/** The board's filters (and its Group by) as a view's spec. */
export function specOf(f: Filters, group: GroupBy): ViewSpec {
  return {
    lanes: [...f.lanes] as Lane[],
    high: f.high,
    tag: f.tag,
    role: f.role,
    asg: [...f.asg],
    teams: f.teams,
    q: f.q,
    notReviewed: f.notReviewed,
    nondev: f.nondev,
    notes: f.notes,
    kind: f.kind,
    project: f.project,
    noProject: f.noProject,
    hideProjects: [...f.hideProjects],
    drafts: f.drafts,
    due: f.due,
    sort: f.sort,
    group: group as ViewGroup,
  };
}

/** A view's spec as the board's filters to open with. */
export function filtersOf(spec: ViewSpec): Filters {
  return { ...initialFilters(), ...spec, lanes: new Set(spec.lanes), asg: [...spec.asg], noProject: !!spec.noProject, hideProjects: [...(spec.hideProjects ?? [])], drafts: spec.drafts ?? "hide" };
}

/** What the board shows as a view: a saved one, or a quick one (worked out for whoever looks). */
export interface OpenView {
  /** the saved view's id, or "quick:verify" */
  id: string;
  name: string;
  spec: ViewSpec;
  /** a quick view: its rule picks the items, and it can only be saved as a new view */
  quick: QuickViewId | null;
  /** the saved view, when it is one */
  saved: SavedViewView | null;
}

/** The view an id names, among the person's views and the quick ones; null when it's none of them. */
export function openViewOf(id: string, views: SavedViewView[] | null): OpenView | null {
  const q = quickOf(id);
  if (q) {
    const def = QUICK_VIEWS.find((x) => x.id === q)!;
    return { id: `${QUICK_PREFIX}${q}`, name: def.name, spec: quickSpec(q), quick: q, saved: null };
  }
  const v = views?.find((x) => x.id === id);
  return v ? { id: v.id, name: v.name, spec: v.spec, quick: null, saved: v } : null;
}

/** The main board's own filters as a spec (what "+ Save current view" saves when no view is open). */
export const DEFAULT_VIEW_SPEC = DEFAULT_SPEC;
